import {
  askUserQuestionManifest,
  createCMSCollectionFieldManifest,
  createCMSCollectionItemsManifest,
  createCMSCollectionManifest,
  createInstructionManifest,
  checkFunctionsCmsAccessManifest,
  createWebhookManifest,
  deleteInstructionManifest,
  deleteUnusedStylesTool,
  deleteWebhookManifest,
  editComponentCodeTool,
  enableFunctionsCmsAccessManifest,
  extractSiteStylesTool,
  findUnusedStylesTool,
  getAllSelectorsFromSiteTool,
  getCMSCollectionDetailsManifest,
  getCMSCollectionItemDetailsManifest,
  editWithPromptManifest,
  extendCanvasManifest,
  imageTransformManifest,
  removeBackgroundManifest,
  getFormInputNameTool,
  getFormInputRequiredTool,
  getFormInputTypeTool,
  getFormNameTool,
  getFormSettingsTool,
  getHtmlFromQuerySelectorsTool,
  getHtmlFromSelectedElementTool,
  getHtmlFromTextTool,
  getPageMetadataManifest,
  getSiteInfoManifest,
  setFormInputNameTool,
  setFormInputRequiredTool,
  setFormInputTypeTool,
  setFormNameTool,
  setFormSettingsTool,
  listCMSCollectionItemsManifest,
  listCMSCollectionsManifest,
  listPagesManifest,
  listSiteBranchesManifest,
  createSiteBranchManifest,
  stageSiteBranchManifest,
  mergeSiteBranchManifest,
  getSiteBranchTaskManifest,
  getSiteBranchFreshnessManifest,
  listSiteBranchChangesManifest,
  discardSiteBranchManifest,
  updateSiteBranchDetailsManifest,
  listWebhooksManifest,
  moveInstructionManifest,
  publishCMSCollectionItemsManifest,
  readPageTreeTool,
  readPageTool,
  readRemoteFileTool,
  editRemoteFileTool,
  editRemoteFileExactTool,
  writeRemoteFileTool,
  writeRemoteFilesTool,
  deleteRemoteFilesTool,
  renderCssTool,
  renderTool,
  applyStructuredBriefTool,
  renderVariablesTool,
  readInstructionManifest,
  searchInstructionsManifest,
  searchStylesTool,
  searchVariablesManifest,
  setComponentCodeTool,
  setCodeComponentStatesTool,
  setComponentNameTool,
  setElementTextContentTool,
  updateCMSCollectionFieldManifest,
  updateCMSCollectionItemsManifest,
  updateCMSCollectionManifest,
  updateInstructionManifest,
  updatePageMetadataManifest,
  listFileTool,
  readFileTool,
  readReferenceFileManifest,
  createTranslationExclusionsManifest,
  deleteTranslationExclusionsManifest,
  listTranslationExclusionsManifest,
  listTargetLanguagesManifest,
  bulkUpdateTranslationExclusionsManifest,
  updateAllMatchingTranslationExclusionsManifest,
  deleteAllMatchingTranslationExclusionsManifest,
  listSiteComponentsManifest,
  getComponentPropertiesManifest,
} from '../tool-manifests';
import {searchKnowledgeBaseManifest} from '../tool-manifests/search-knowledge-base';
import type {
  AnyToolRegistryManifest,
  ToolArgumentType,
} from './base-tool-types';

export interface ToolRegistryGroup {
  /**
   * A plain language ID for the group. This will be provided to the LLM.
   */
  name: string;
  /**
   * The LLM will select a tool group based on this description.
   */
  description: string;
  /**
   * This prompt is added to the system prompt when this tool group is selected.
   * It should have information about how the tools in the group relate to one
   * another. The systemPrompt should also include instructions for artifacts
   * that are created by the tools in the group. For example, if a tool creates
   * a CMS collection, the systemPrompt should include information about how the
   * CMS collection should be structured.
   */
  systemPrompt?: string;
  tools: ToolRegistryItem[];
}

export type ToolId =
  | 'ask-user-question'
  | 'bulk-update-translation-exclusions'
  | 'create-cms-collection-field'
  | 'create-cms-collection-items'
  | 'create-cms-collection'
  | 'create-instruction'
  | 'create-translation-exclusions'
  | 'create-webhook'
  | 'delete-all-matching-translation-exclusions'
  | 'delete-instruction'
  | 'delete-translation-exclusions'
  | 'delete-unused-styles'
  | 'delete-webhook'
  | 'edit-component-code'
  | 'edit-remote-file'
  | 'edit-remote-file-exact'
  | 'edit-with-prompt'
  | 'enable-functions-cms-access'
  | 'check-functions-cms-access'
  | 'extend-canvas'
  | 'extract-site-styles'
  | 'find-unused-styles'
  | 'get-all-selectors-from-site'
  | 'get-cms-collection-details'
  | 'get-cms-collection-item-details'
  | 'get-component-properties'
  | 'get-form-input-name'
  | 'get-form-input-required'
  | 'get-form-input-type'
  | 'get-form-name'
  | 'get-form-settings'
  | 'get-html-from-query-selectors'
  | 'get-html-from-selected-element'
  | 'get-html-from-text'
  | 'get-page-metadata'
  | 'get-site-info'
  | 'image-transform'
  | 'list-cms-collection-items'
  | 'list-cms-collections'
  | 'list-file'
  | 'list-pages'
  | 'list-site-branches'
  | 'create-site-branch'
  | 'stage-site-branch'
  | 'merge-site-branch'
  | 'get-site-branch-task'
  | 'get-site-branch-freshness'
  | 'list-site-branch-changes'
  | 'discard-site-branch'
  | 'update-site-branch-details'
  | 'list-site-components'
  | 'list-target-languages'
  | 'list-translation-exclusions'
  | 'list-webhooks'
  | 'move-instruction'
  | 'publish-cms-collection-items'
  | 'read-file'
  | 'read-instruction'
  | 'read-page'
  | 'read-page-tree'
  | 'read-reference-file'
  | 'read-remote-file'
  | 'remove-background'
  | 'render-css'
  | 'render'
  | 'apply-structured-brief'
  | 'render-variables'
  | 'search-instructions'
  | 'search-knowledge-base'
  | 'search-styles'
  | 'search-variables'
  | 'set-component-code'
  | 'set-code-component-states'
  | 'set-component-name'
  | 'set-element-text-content'
  | 'set-form-input-name'
  | 'set-form-input-required'
  | 'set-form-input-type'
  | 'set-form-name'
  | 'set-form-settings'
  | 'update-all-matching-translation-exclusions'
  | 'update-cms-collection-field'
  | 'update-cms-collection-items'
  | 'update-cms-collection'
  | 'update-instruction'
  | 'update-page-metadata'
  | 'write-remote-file'
  | 'write-remote-files'
  | 'delete-remote-files';

export const toolRegistryManifests = {
  [askUserQuestionManifest.id]: askUserQuestionManifest,
  [bulkUpdateTranslationExclusionsManifest.id]:
    bulkUpdateTranslationExclusionsManifest,
  [createCMSCollectionFieldManifest.id]: createCMSCollectionFieldManifest,
  [createCMSCollectionItemsManifest.id]: createCMSCollectionItemsManifest,
  [createCMSCollectionManifest.id]: createCMSCollectionManifest,
  [createInstructionManifest.id]: createInstructionManifest,
  [createTranslationExclusionsManifest.id]: createTranslationExclusionsManifest,
  [createWebhookManifest.id]: createWebhookManifest,
  [deleteAllMatchingTranslationExclusionsManifest.id]:
    deleteAllMatchingTranslationExclusionsManifest,
  [deleteInstructionManifest.id]: deleteInstructionManifest,
  [deleteTranslationExclusionsManifest.id]: deleteTranslationExclusionsManifest,
  [deleteUnusedStylesTool.id]: deleteUnusedStylesTool,
  [deleteWebhookManifest.id]: deleteWebhookManifest,
  [editComponentCodeTool.id]: editComponentCodeTool,
  [editRemoteFileTool.id]: editRemoteFileTool,
  [editRemoteFileExactTool.id]: editRemoteFileExactTool,
  [editWithPromptManifest.id]: editWithPromptManifest,
  [enableFunctionsCmsAccessManifest.id]: enableFunctionsCmsAccessManifest,
  [checkFunctionsCmsAccessManifest.id]: checkFunctionsCmsAccessManifest,
  [extendCanvasManifest.id]: extendCanvasManifest,
  [extractSiteStylesTool.id]: extractSiteStylesTool,
  [findUnusedStylesTool.id]: findUnusedStylesTool,
  [getAllSelectorsFromSiteTool.id]: getAllSelectorsFromSiteTool,
  [getCMSCollectionDetailsManifest.id]: getCMSCollectionDetailsManifest,
  [getCMSCollectionItemDetailsManifest.id]: getCMSCollectionItemDetailsManifest,
  [getComponentPropertiesManifest.id]: getComponentPropertiesManifest,
  [getFormInputNameTool.id]: getFormInputNameTool,
  [getFormInputRequiredTool.id]: getFormInputRequiredTool,
  [getFormInputTypeTool.id]: getFormInputTypeTool,
  [getFormNameTool.id]: getFormNameTool,
  [getFormSettingsTool.id]: getFormSettingsTool,
  [getHtmlFromQuerySelectorsTool.id]: getHtmlFromQuerySelectorsTool,
  [getHtmlFromSelectedElementTool.id]: getHtmlFromSelectedElementTool,
  [getHtmlFromTextTool.id]: getHtmlFromTextTool,
  [getPageMetadataManifest.id]: getPageMetadataManifest,
  [getSiteInfoManifest.id]: getSiteInfoManifest,
  [imageTransformManifest.id]: imageTransformManifest,
  [listCMSCollectionItemsManifest.id]: listCMSCollectionItemsManifest,
  [listCMSCollectionsManifest.id]: listCMSCollectionsManifest,
  [listFileTool.id]: listFileTool,
  [listPagesManifest.id]: listPagesManifest,
  [listSiteBranchesManifest.id]: listSiteBranchesManifest,
  [createSiteBranchManifest.id]: createSiteBranchManifest,
  [stageSiteBranchManifest.id]: stageSiteBranchManifest,
  [mergeSiteBranchManifest.id]: mergeSiteBranchManifest,
  [getSiteBranchTaskManifest.id]: getSiteBranchTaskManifest,
  [getSiteBranchFreshnessManifest.id]: getSiteBranchFreshnessManifest,
  [listSiteBranchChangesManifest.id]: listSiteBranchChangesManifest,
  [discardSiteBranchManifest.id]: discardSiteBranchManifest,
  [updateSiteBranchDetailsManifest.id]: updateSiteBranchDetailsManifest,
  [listSiteComponentsManifest.id]: listSiteComponentsManifest,
  [listTargetLanguagesManifest.id]: listTargetLanguagesManifest,
  [listTranslationExclusionsManifest.id]: listTranslationExclusionsManifest,
  [listWebhooksManifest.id]: listWebhooksManifest,
  [moveInstructionManifest.id]: moveInstructionManifest,
  [publishCMSCollectionItemsManifest.id]: publishCMSCollectionItemsManifest,
  [readFileTool.id]: readFileTool,
  [readInstructionManifest.id]: readInstructionManifest,
  [readPageTool.id]: readPageTool,
  [readPageTreeTool.id]: readPageTreeTool,
  [readReferenceFileManifest.id]: readReferenceFileManifest,
  [readRemoteFileTool.id]: readRemoteFileTool,
  [removeBackgroundManifest.id]: removeBackgroundManifest,
  [renderCssTool.id]: renderCssTool,
  [renderTool.id]: renderTool,
  [applyStructuredBriefTool.id]: applyStructuredBriefTool,
  [renderVariablesTool.id]: renderVariablesTool,
  [searchInstructionsManifest.id]: searchInstructionsManifest,
  [searchKnowledgeBaseManifest.id]: searchKnowledgeBaseManifest,
  [searchStylesTool.id]: searchStylesTool,
  [searchVariablesManifest.id]: searchVariablesManifest,
  [setComponentCodeTool.id]: setComponentCodeTool,
  [setCodeComponentStatesTool.id]: setCodeComponentStatesTool,
  [setComponentNameTool.id]: setComponentNameTool,
  [setElementTextContentTool.id]: setElementTextContentTool,
  [setFormInputNameTool.id]: setFormInputNameTool,
  [setFormInputRequiredTool.id]: setFormInputRequiredTool,
  [setFormInputTypeTool.id]: setFormInputTypeTool,
  [setFormNameTool.id]: setFormNameTool,
  [setFormSettingsTool.id]: setFormSettingsTool,
  [updateAllMatchingTranslationExclusionsManifest.id]:
    updateAllMatchingTranslationExclusionsManifest,
  [updateCMSCollectionFieldManifest.id]: updateCMSCollectionFieldManifest,
  [updateCMSCollectionItemsManifest.id]: updateCMSCollectionItemsManifest,
  [updateCMSCollectionManifest.id]: updateCMSCollectionManifest,
  [updateInstructionManifest.id]: updateInstructionManifest,
  [updatePageMetadataManifest.id]: updatePageMetadataManifest,
  [writeRemoteFileTool.id]: writeRemoteFileTool,
  [writeRemoteFilesTool.id]: writeRemoteFilesTool,
  [deleteRemoteFilesTool.id]: deleteRemoteFilesTool,
} satisfies Record<ToolId, AnyToolRegistryManifest>;

export type ToolRegistryItem = (typeof toolRegistryManifests)[ToolId];

export type ToolArgumentTypeById<TId extends ToolId> =
  Extract<ToolRegistryItem, {id: TId}> extends infer Tool
    ? Tool extends ToolRegistryItem
      ? ToolArgumentType<Tool>
      : never
    : never;
